import type { EthersError, GasLimitError, ReturnValue } from './types';
import { RETURN_VALUE_ERROR_CODES } from './constants';
import { getNestedLevelKnownError } from './utils/getNestedLevelKnownError';
import { getTopLevelKnownError } from './utils/getTopLevelKnownError';
import { getEthersGasLimitError } from './utils/getEthersGasLimitError';
import { getTenderlyGasLimitError } from './utils/getTenderlyGasLimitError';

export function getParsedError(error: EthersError): ReturnValue {
	const topLevelEthersError = error;
	const nestedLevelEthersError = error.error;
	if (error.name && error.message) {
		return {
			title: error.name,
			description: error.message
		};
	}

	// Handle tenderly gas estimation errors
	if (error.type === 'tenderly') {
		const tenderlyGasLimitEstimationError = getTenderlyGasLimitError(error);
		return tenderlyGasLimitEstimationError;
	}

	// Handle ethers gas estimation errors
	if (error.type === 'ethers') {
		const ethersGasLimitEstimationError = getEthersGasLimitError(error as GasLimitError);
		return ethersGasLimitEstimationError;
	}

	// Handle top level known error codes
	const topLevelKnownErrorCode = getTopLevelKnownError(topLevelEthersError);
	if (topLevelKnownErrorCode !== undefined) {
		return topLevelKnownErrorCode;
	}

	// Handle nested level known error codes
	if (nestedLevelEthersError !== undefined) {
		const nestedLevelKnownErrorCode = getNestedLevelKnownError(nestedLevelEthersError);
		if (nestedLevelKnownErrorCode !== undefined) {
			return nestedLevelKnownErrorCode;
		}
	}

	// Check for ran out of gas error
	if (topLevelEthersError.transaction !== undefined && topLevelEthersError.receipt !== undefined) {
		const transactionGasLimit = topLevelEthersError.transaction.gasLimit;
		const receiptGasUsed = topLevelEthersError.receipt.gasUsed;

		if (receiptGasUsed.gte(transactionGasLimit)) {
			return {
				title: RETURN_VALUE_ERROR_CODES.TRANSACTION_RAN_OUT_OF_GAS,
				description: topLevelEthersError.transaction.gasLimit.toString()
			};
		}
	}

	// If we reach here, we should improve previous parsing to not reach this point any longer

	// Try providing some context via the nested level error message
	if (error.error !== undefined && error.error.message !== undefined) {
		return {
			title: RETURN_VALUE_ERROR_CODES.FAILED_TO_PARSE_ERROR,
			description: error.error.message
		};
	}

	if (
		topLevelEthersError.code !== undefined &&
		topLevelEthersError.message !== undefined &&
		topLevelEthersError.name !== undefined
	) {
		return {
			title: topLevelEthersError.name,
			description: topLevelEthersError.message
		};
	}

	// Try providing some context via the top level error code
	if (topLevelEthersError.code !== undefined) {
		return {
			title: RETURN_VALUE_ERROR_CODES.FAILED_TO_PARSE_ERROR,
			description: topLevelEthersError.code.toString()
		};
	}

	if (error.message) {
		return {
			title: RETURN_VALUE_ERROR_CODES.FAILED_TO_PARSE_ERROR,
			description: error.message
		};
	}

	return {
		title: RETURN_VALUE_ERROR_CODES.FAILED_TO_PARSE_ERROR,
		description: JSON.stringify(error, Object.getOwnPropertyNames(error))
	};
}
